import { ReactNode, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { api, MarketplaceArcheDetail } from '@/lib/api';
import { useAuth } from '@/auth';
import { ArcheContext, ArcheContextValue } from '@/sdui/hooks/useArcheContext';
import { ArcheActionContext, ArcheActionValue } from '@/sdui/hooks/useArcheAction';
import { ArcheNavigationContext, ArcheNavigationValue } from '@/sdui/hooks/useArcheNavigation';
import type {
  UIDefinition,
  BindingContext,
  ActionDefinition,
  ComputeAction,
  AIInvokeAction,
  FileWriteAction,
  FileReadAction,
  FileDownloadAction,
} from '@fias/sdui-types';
import { MAX_ACTION_DELAY_MS } from '@/lib/sdui-constants';
import { evaluateExpression, ExpressionContext } from '@fias/expression-eval';
import { getWebSocket, FiasWebSocket } from '@/lib/websocket';

interface ArcheProviderProps {
  archeId: string;
  children: ReactNode;
  /** Skip API fetch, use this UI definition directly (for builder preview) */
  externalUiDefinition?: UIDefinition | null;
  /** Arche name when using external UI definition */
  archeName?: string;
}

// Create a "Coming Soon" UI definition for placeholder arches
function createComingSoonUI(arche: MarketplaceArcheDetail): UIDefinition {
  return {
    version: '1.0',
    defaultPage: 'coming-soon',
    pages: {
      'coming-soon': {
        id: 'coming-soon',
        title: arche.name,
        description: arche.simpleDescription,
        layout: [
          {
            type: 'Container',
            id: 'container-1',
            props: { maxWidth: 'md', padding: 'lg' },
            children: [
              {
                type: 'Card',
                id: 'card-1',
                props: { padding: 'lg' },
                children: [
                  {
                    type: 'Heading',
                    id: 'heading-1',
                    props: { level: 2, text: arche.name },
                  },
                  {
                    type: 'Badge',
                    id: 'badge-1',
                    props: { text: 'Coming Soon', variant: 'secondary' },
                  },
                  {
                    type: 'Text',
                    id: 'description-1',
                    props: {
                      text: arche.expandedDescription || arche.simpleDescription,
                      className: 'mt-4',
                    },
                  },
                  {
                    type: 'Text',
                    id: 'cta-1',
                    props: {
                      text: 'This arche is currently under development. Check back soon!',
                      className: 'mt-6 text-muted-foreground',
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
    },
  };
}

export function ArcheProvider({
  archeId,
  children,
  externalUiDefinition,
  archeName: externalArcheName,
}: ArcheProviderProps) {
  const { user } = useAuth();

  // State
  const [arche, setArche] = useState<MarketplaceArcheDetail | null>(null);
  const [ui, setUi] = useState<UIDefinition | null>(null);
  const [currentPage, setCurrentPage] = useState<string>('');
  const [pageHistory, setPageHistory] = useState<string[]>([]);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [inputValues, setInputValues] = useState<Record<string, unknown>>({});
  const [outputValues, setOutputValues] = useState<Record<string, unknown>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isExecuting, setIsExecuting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  // AI streaming state
  const [isAIStreaming, setIsAIStreaming] = useState(false);
  const [aiStreamingText, setAiStreamingText] = useState<Record<string, string>>({});
  const [aiThinkingText, setAiThinkingText] = useState<Record<string, string>>({});
  const currentStreamRequestIdRef = useRef<string | null>(null);

  // Track active stream for stop functionality
  const activeStreamRef = useRef<{
    cleanup: () => void;
    getAccumulatedText: () => string;
    outputKey: string;
  } | null>(null);

  // WebSocket instance reference
  const wsRef = useRef<FiasWebSocket | null>(null);

  // Ref for executeAIInvokeAction to break circular dependency with executeClientAction
  const executeAIInvokeActionRef = useRef<
    | ((
        action: AIInvokeAction,
        payload: Record<string, unknown>,
        chainDepth?: number,
      ) => Promise<void>)
    | null
  >(null);

  // Ref for executeClientAction to break forward reference from triggerAction
  const executeClientActionRef = useRef<
    ((action: ActionDefinition, payload?: Record<string, unknown>) => void) | null
  >(null);

  // Track pending timeouts for cleanup on unmount
  const pendingTimeoutsRef = useRef<Set<ReturnType<typeof setTimeout>>>(new Set());

  // Track which pages have already had their onLoad actions executed
  const pagesLoadedRef = useRef<Set<string>>(new Set());

  // AI chain depth limit to prevent infinite recursion
  const MAX_AI_CHAIN_DEPTH = 5;

  // Cleanup all pending timeouts on unmount
  useEffect(() => {
    return () => {
      pendingTimeoutsRef.current.forEach((timeout) => clearTimeout(timeout));
      pendingTimeoutsRef.current.clear();
    };
  }, []);

  // Stable requestId for external mode — generated once per mount, never reset on UI updates
  const externalRequestIdRef = useRef<string | null>(null);

  // SessionStorage key for persisting preview input state across refreshes (external mode only)
  const storageKey = externalUiDefinition !== undefined ? `fias-preview-state:${archeId}` : null;

  // Load arche data and UI (API-fetch mode only — skipped when externalUiDefinition is provided)
  useEffect(() => {
    if (externalUiDefinition !== undefined) {
      // External mode is handled by the sync effect below
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    async function loadArche() {
      setIsLoading(true);
      setError(null);

      try {
        // First get arche details
        const archeDetail = await api.getMarketplaceArche(archeId);
        if (cancelled) return;
        setArche(archeDetail);

        // Try to get the arche UI
        try {
          const uiResult = await api.getArcheUI(archeId);
          if (cancelled) return;

          if (uiResult.ui) {
            const uiDef = uiResult.ui as UIDefinition;
            setUi(uiDef);
            setCurrentPage(uiDef.defaultPage || Object.keys(uiDef.pages)[0]);
            setRequestId(uiResult.context.requestId);
            // Initialize input values from UI definition
            if (uiDef.initialInput) {
              setInputValues(uiDef.initialInput);
            }
          } else {
            // No UI definition, use coming soon placeholder
            const comingSoonUi = createComingSoonUI(archeDetail);
            setUi(comingSoonUi);
            setCurrentPage(comingSoonUi.defaultPage);
          }
        } catch {
          // UI fetch failed, use coming soon placeholder
          const comingSoonUi = createComingSoonUI(archeDetail);
          setUi(comingSoonUi);
          setCurrentPage(comingSoonUi.defaultPage);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load arche');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    }

    loadArche();

    return () => {
      cancelled = true;
    };
  }, [archeId]); // Only archeId — external mode doesn't use this effect

  // Sync external UI definition into state.
  // Generates requestId once and preserves user state (inputValues, outputValues, pageHistory)
  // across subsequent UI updates from the builder.
  useEffect(() => {
    if (externalUiDefinition === undefined) return;
    if (!externalUiDefinition) return;

    setUi(externalUiDefinition);

    const defaultPage =
      externalUiDefinition.defaultPage || Object.keys(externalUiDefinition.pages)[0];
    setCurrentPage((prev) => {
      // On first load (no previous page) or if current page was removed, go to default
      if (!prev || !externalUiDefinition.pages[prev]) return defaultPage;
      return prev;
    });

    // Generate requestId only once — subsequent UI updates keep the same one
    // so AI sessions persist across builder edits
    if (!externalRequestIdRef.current) {
      externalRequestIdRef.current = crypto.randomUUID();
      setRequestId(externalRequestIdRef.current);

      // Restore persisted preview state if available, otherwise use initialInput
      let restored = false;
      if (storageKey) {
        try {
          const saved = sessionStorage.getItem(storageKey);
          if (saved) {
            const parsed = JSON.parse(saved) as Record<string, unknown>;
            setInputValues(parsed);
            restored = true;
          }
        } catch {
          // Corrupt or unavailable — fall through to initialInput
        }
      }
      if (!restored && externalUiDefinition.initialInput) {
        setInputValues(externalUiDefinition.initialInput);
      }
    }
  }, [externalUiDefinition, storageKey]);

  // Persist preview input state to sessionStorage on changes (external mode only).
  // Debounced to avoid thrashing storage on rapid updates (e.g., typing).
  useEffect(() => {
    if (!storageKey) return;
    // Skip saving until external mode is fully initialized
    if (!externalRequestIdRef.current) return;

    const timer = setTimeout(() => {
      try {
        sessionStorage.setItem(storageKey, JSON.stringify(inputValues));
      } catch {
        // Storage full or unavailable — non-critical
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [inputValues, storageKey]);

  // Build binding context
  const bindingContext: BindingContext = useMemo(
    () => ({
      input: inputValues,
      output: outputValues,
      user: {
        id: user?.userId || '',
        displayName: user?.email || 'Anonymous',
      },
      arche: {
        id: archeId,
        name: arche?.name || '',
      },
      request: {
        id: requestId || '',
        currentPage,
      },
    }),
    [inputValues, outputValues, user, archeId, arche, requestId, currentPage],
  );

  // Navigation handlers
  const navigateTo = useCallback(
    (pageId: string) => {
      setPageHistory((prev) => [...prev, currentPage]);
      setCurrentPage(pageId);
    },
    [currentPage],
  );

  const goBack = useCallback(() => {
    if (pageHistory.length > 0) {
      const previousPage = pageHistory[pageHistory.length - 1];
      setPageHistory((prev) => prev.slice(0, -1));
      setCurrentPage(previousPage);
    }
  }, [pageHistory]);

  // Client-side action types that should be handled locally, not sent to the server
  const CLIENT_ACTION_TYPES = [
    'updateContext',
    'navigate',
    'compute',
    'aiInvoke',
    'fileWrite',
    'fileRead',
    'fileDownload',
  ];

  // Action handlers
  const triggerAction = useCallback(
    async (actionId: string, payload: Record<string, unknown>) => {
      // Check if this is a client-side action we can handle locally
      const pageActions = ui?.pages?.[currentPage]?.actions as
        | Record<string, ActionDefinition>
        | undefined;
      const action = pageActions?.[actionId];
      if (action && CLIENT_ACTION_TYPES.includes(action.type)) {
        executeClientActionRef.current?.(action, payload);
        return;
      }

      if (!requestId) {
        console.warn('Cannot trigger action without request ID');
        return;
      }

      setIsExecuting(true);
      setActionError(null);

      try {
        const result = await api.submitAction(archeId, {
          requestId,
          actionId,
          pageId: currentPage,
          payload,
        });

        if (result.nextPage) {
          navigateTo(result.nextPage);
        }

        if (result.updatedContext) {
          setOutputValues((prev) => ({ ...prev, ...result.updatedContext }));
        }
      } catch (err) {
        setActionError(err instanceof Error ? err.message : 'Action failed');
      } finally {
        setIsExecuting(false);
      }
    },
    [archeId, requestId, currentPage, navigateTo, ui],
  );

  const submitForm = useCallback(
    (formData: Record<string, unknown>) => {
      triggerAction('submit', formData);
    },
    [triggerAction],
  );

  const updateInput = useCallback((key: string, value: unknown) => {
    setInputValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Execute a compute action - evaluate expressions and update input values
  const executeComputeAction = useCallback(
    (action: ComputeAction, payload: Record<string, unknown>) => {
      // Build expression context from current state
      const exprContext: ExpressionContext = {
        input: inputValues,
        output: outputValues,
        payload,
      };

      // Check condition if present
      if (action.condition) {
        const condResult = evaluateExpression(action.condition, exprContext);
        if (!condResult.success || !condResult.value) {
          // Condition not met, skip computations
          return;
        }
      }

      // Execute all computations and collect updates
      const updates: Record<string, unknown> = {};
      for (const [key, expression] of Object.entries(action.computations)) {
        const result = evaluateExpression(expression, exprContext);
        if (result.success) {
          updates[key] = result.value;
        } else {
          console.warn(`Compute expression failed for ${key}: ${result.error}`);
        }
      }

      // Apply updates to input values
      if (Object.keys(updates).length > 0) {
        setInputValues((prev) => ({ ...prev, ...updates }));
      }
    },
    [inputValues, outputValues],
  );

  // Execute an AI invoke action - call AI endpoint and stream response
  const executeAIInvokeAction = useCallback(
    async (action: AIInvokeAction, payload: Record<string, unknown>, chainDepth = 0) => {
      if (!requestId || !ui?.aiConfigs?.[action.configId]) {
        console.warn('Cannot execute AI invoke without request ID or AI config');
        return;
      }

      if (chainDepth >= MAX_AI_CHAIN_DEPTH) {
        console.error(`AI chain depth exceeded maximum of ${MAX_AI_CHAIN_DEPTH}. Stopping chain.`);
        setActionError(`AI workflow chain exceeded maximum depth of ${MAX_AI_CHAIN_DEPTH}`);
        return;
      }

      setIsAIStreaming(true);
      setAiStreamingText((prev) => ({ ...prev, [action.configId]: '' }));
      setAiThinkingText((prev) => ({ ...prev, [action.configId]: '' }));
      setActionError(null);

      // Track accumulated text for final output
      let accumulatedText = '';

      try {
        // Build input from inputMapping
        const mappedInput: Record<string, unknown> = {};
        for (const [key, binding] of Object.entries(action.inputMapping)) {
          if (typeof binding === 'string' && binding.startsWith('{{') && binding.endsWith('}}')) {
            // Parse binding expression like {{input.message}}
            const bindingPath = binding.slice(2, -2).trim();
            const parts = bindingPath.split('.');
            if (parts[0] === 'input' && parts[1]) {
              mappedInput[key] = inputValues[parts[1]];
            } else if (parts[0] === 'output' && parts[1]) {
              mappedInput[key] = outputValues[parts[1]];
            } else if (parts[0] === 'payload' && parts[1]) {
              mappedInput[key] = payload[parts[1]];
            }
          } else {
            mappedInput[key] = binding;
          }
        }

        // Call AI invoke endpoint
        const response = await api.invokeAI(archeId, {
          requestId,
          configId: action.configId,
          input: mappedInput,
          appendToHistory: action.appendToHistory,
        });

        if (!response.success || !response.streamRequestId) {
          throw new Error('Failed to start AI invocation');
        }

        const streamRequestId = response.streamRequestId;
        currentStreamRequestIdRef.current = streamRequestId;

        // Get WebSocket instance
        try {
          const ws = wsRef.current || getWebSocket();
          wsRef.current = ws;

          if (!ws.isConnected()) {
            console.warn('WebSocket not connected, AI streaming may not work');
            setIsAIStreaming(false);
            return;
          }

          // Set up callbacks for this stream
          const cleanupCallbacks: (() => void)[] = [];

          // Token callback
          const removeTokenCb = ws.onToken((_nodeId, text, reqId) => {
            if (reqId === streamRequestId) {
              accumulatedText += text;
              setAiStreamingText((prev) => ({
                ...prev,
                [action.configId]: (prev[action.configId] || '') + text,
              }));
              // Update output values in real-time so any bound component
              // (Text, Markdown, etc.) shows streaming text live
              if (action.outputKey) {
                setOutputValues((prev) => ({ ...prev, [action.outputKey]: accumulatedText }));
              }
            }
          });
          cleanupCallbacks.push(removeTokenCb);

          // Thinking callback
          const removeThinkingCb = ws.onThinking((_nodeId, text, reqId) => {
            if (reqId === streamRequestId) {
              setAiThinkingText((prev) => ({
                ...prev,
                [action.configId]: (prev[action.configId] || '') + text,
              }));
            }
          });
          cleanupCallbacks.push(removeThinkingCb);

          // Complete callback
          const removeCompleteCb = ws.onComplete((reqId) => {
            if (reqId === streamRequestId) {
              // Guard: if stopAIStreaming already ran, no-op
              if (!activeStreamRef.current) return;
              activeStreamRef.current = null;

              // Update output values with the accumulated response
              if (action.outputKey && accumulatedText) {
                setOutputValues((prev) => ({ ...prev, [action.outputKey]: accumulatedText }));
              }

              // Cleanup
              cleanupCallbacks.forEach((fn) => fn());
              ws.unsubscribe();
              setIsAIStreaming(false);
              currentStreamRequestIdRef.current = null;

              // Execute success callback
              if (action.onSuccess?.actionId) {
                // Use setTimeout to avoid circular dependency issues
                setTimeout(() => {
                  const pageActions = ui?.pages?.[currentPage]?.actions as
                    | Record<string, ActionDefinition>
                    | undefined;
                  if (pageActions?.[action.onSuccess!.actionId]) {
                    const successAction = pageActions[action.onSuccess!.actionId];
                    if (
                      successAction.type === 'updateContext' ||
                      successAction.type === 'navigate' ||
                      successAction.type === 'compute' ||
                      successAction.type === 'fileWrite' ||
                      successAction.type === 'fileRead' ||
                      successAction.type === 'fileDownload'
                    ) {
                      executeClientAction(successAction, payload);
                    } else if (successAction.type === 'aiInvoke') {
                      // Chain to next AI invocation with incremented depth
                      executeAIInvokeActionRef.current?.(successAction, payload, chainDepth + 1);
                    }
                  }
                }, 0);
              }
            }
          });
          cleanupCallbacks.push(removeCompleteCb);

          // Error callback
          const removeErrorCb = ws.onError((_code, message) => {
            // Guard: if stopAIStreaming already ran, no-op
            if (!activeStreamRef.current) return;
            activeStreamRef.current = null;

            setActionError(message || 'AI invocation failed');
            // Cleanup
            cleanupCallbacks.forEach((fn) => fn());
            ws.unsubscribe();
            setIsAIStreaming(false);
            currentStreamRequestIdRef.current = null;

            // Execute error callback
            if (action.onError?.actionId) {
              setTimeout(() => {
                const pageActions = ui?.pages?.[currentPage]?.actions as
                  | Record<string, ActionDefinition>
                  | undefined;
                if (pageActions?.[action.onError!.actionId]) {
                  const errorAction = pageActions[action.onError!.actionId];
                  if (
                    errorAction.type === 'updateContext' ||
                    errorAction.type === 'navigate' ||
                    errorAction.type === 'compute' ||
                    errorAction.type === 'fileWrite' ||
                    errorAction.type === 'fileRead' ||
                    errorAction.type === 'fileDownload'
                  ) {
                    executeClientAction(errorAction, payload);
                  } else if (errorAction.type === 'aiInvoke') {
                    executeAIInvokeActionRef.current?.(errorAction, payload, chainDepth + 1);
                  }
                }
              }, 0);
            }
          });
          cleanupCallbacks.push(removeErrorCb);

          // Register active stream ref for stop functionality
          activeStreamRef.current = {
            cleanup: () => {
              cleanupCallbacks.forEach((fn) => fn());
              ws.unsubscribe();
            },
            getAccumulatedText: () => accumulatedText,
            outputKey: action.outputKey,
          };

          // Subscribe to the stream
          ws.subscribe(streamRequestId);
        } catch (wsError) {
          console.error('WebSocket error:', wsError);
          setIsAIStreaming(false);
        }
      } catch (err) {
        console.error('AI invoke error:', err);
        setActionError(err instanceof Error ? err.message : 'AI invocation failed');
        setIsAIStreaming(false);
      }
    },
    [archeId, requestId, ui, currentPage, inputValues, outputValues],
  );

  // Update ref when executeAIInvokeAction changes
  useEffect(() => {
    executeAIInvokeActionRef.current = executeAIInvokeAction;
  }, [executeAIInvokeAction]);

  // Stop the current AI stream, saving partial text to output
  const stopAIStreaming = useCallback(() => {
    if (!activeStreamRef.current) return;

    const { cleanup, getAccumulatedText, outputKey } = activeStreamRef.current;
    activeStreamRef.current = null;

    // Save partial text to output so the streaming-completion effect picks it up
    const partialText = getAccumulatedText();
    if (outputKey && partialText) {
      setOutputValues((prev) => ({ ...prev, [outputKey]: partialText }));
    }

    cleanup();
    setIsAIStreaming(false);
    currentStreamRequestIdRef.current = null;
  }, []);

  // Helper: resolve binding expressions in a string value
  const resolveBinding = useCallback(
    (value: string): string => {
      return value.replace(/\{\{([^}]+)\}\}/g, (_match, expr: string) => {
        const parts = expr.trim().split('.');
        if (parts[0] === 'input' && parts[1]) return String(inputValues[parts[1]] ?? '');
        if (parts[0] === 'output' && parts[1]) return String(outputValues[parts[1]] ?? '');
        return '';
      });
    },
    [inputValues, outputValues],
  );

  // Execute a file write action
  const executeFileWriteAction = useCallback(
    async (action: FileWriteAction, payload: Record<string, unknown>) => {
      try {
        const filename = resolveBinding(action.filename);
        const content = String(
          outputValues[action.contentKey] ?? inputValues[action.contentKey] ?? '',
        );
        const section = action.section ? resolveBinding(action.section) : undefined;
        const find = action.find ? resolveBinding(action.find) : undefined;

        const result = await api.archeFileWrite(archeId, {
          filename,
          content,
          format: action.format,
          mode: action.mode,
          separator: action.separator,
          section,
          find,
          requestId: requestId || undefined,
        });

        if (action.outputFileIdKey && result.fileId) {
          setOutputValues((prev) => ({ ...prev, [action.outputFileIdKey!]: result.fileId }));
        }

        // Execute success callback
        if (action.onSuccess?.actionId) {
          const pageActions = ui?.pages?.[currentPage]?.actions as
            | Record<string, ActionDefinition>
            | undefined;
          if (pageActions?.[action.onSuccess.actionId]) {
            executeClientAction(pageActions[action.onSuccess.actionId], payload);
          }
        }
      } catch (err) {
        console.error('File write error:', err);
        setActionError(err instanceof Error ? err.message : 'File write failed');
        if (action.onError?.actionId) {
          const pageActions = ui?.pages?.[currentPage]?.actions as
            | Record<string, ActionDefinition>
            | undefined;
          if (pageActions?.[action.onError.actionId]) {
            executeClientAction(pageActions[action.onError.actionId], payload);
          }
        }
      }
    },
    [archeId, requestId, ui, currentPage, inputValues, outputValues, resolveBinding],
  );

  // Execute a file read action
  const executeFileReadAction = useCallback(
    async (action: FileReadAction, payload: Record<string, unknown>) => {
      try {
        const filename = action.filename ? resolveBinding(action.filename) : undefined;
        const fileId = action.fileId ? resolveBinding(action.fileId) : undefined;

        const result = await api.archeFileRead(archeId, { filename, fileId });
        setOutputValues((prev) => ({ ...prev, [action.outputKey]: result.content }));

        // Execute success callback
        if (
          action.onSuccess &&
          typeof action.onSuccess === 'object' &&
          'actionId' in action.onSuccess
        ) {
          const pageActions = ui?.pages?.[currentPage]?.actions as
            | Record<string, ActionDefinition>
            | undefined;
          if (pageActions?.[action.onSuccess.actionId]) {
            executeClientAction(pageActions[action.onSuccess.actionId], payload);
          }
        }
      } catch (err) {
        const errorMode = typeof action.onError === 'string' ? action.onError : 'showError';
        if (errorMode === 'ignore') {
          // Silently ignore - file doesn't exist or read failed
          return;
        }
        console.error('File read error:', err);
        setActionError(err instanceof Error ? err.message : 'File read failed');
        if (typeof action.onError === 'object' && action.onError && 'actionId' in action.onError) {
          const pageActions = ui?.pages?.[currentPage]?.actions as
            | Record<string, ActionDefinition>
            | undefined;
          if (pageActions?.[action.onError.actionId]) {
            executeClientAction(pageActions[action.onError.actionId], payload);
          }
        }
      }
    },
    [archeId, ui, currentPage, resolveBinding],
  );

  // Execute a file download action
  const executeFileDownloadAction = useCallback(
    async (action: FileDownloadAction) => {
      try {
        const filename = action.filename ? resolveBinding(action.filename) : undefined;
        const fileId = action.fileId ? resolveBinding(action.fileId) : undefined;

        const result = await api.archeFileDownloadUrl(archeId, { filename, fileId });
        if (result.downloadUrl) {
          // Trigger browser download
          const a = document.createElement('a');
          a.href = result.downloadUrl;
          a.download = result.filename || filename || 'download';
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        }
      } catch (err) {
        console.error('File download error:', err);
        setActionError(err instanceof Error ? err.message : 'File download failed');
      }
    },
    [archeId, resolveBinding],
  );

  // Execute a client-side action with optional delay
  const executeClientAction = useCallback(
    (action: ActionDefinition, payload: Record<string, unknown> = {}) => {
      const executeAction = () => {
        if (action.type === 'updateContext') {
          setInputValues((prev) => ({ ...prev, ...action.updates }));
        } else if (action.type === 'navigate') {
          navigateTo(action.targetPage);
        } else if (action.type === 'compute') {
          executeComputeAction(action, payload);
        } else if (action.type === 'aiInvoke') {
          // Use ref to avoid circular dependency
          executeAIInvokeActionRef.current?.(action, payload);
        } else if (action.type === 'fileWrite') {
          executeFileWriteAction(action, payload);
        } else if (action.type === 'fileRead') {
          executeFileReadAction(action, payload);
        } else if (action.type === 'fileDownload') {
          executeFileDownloadAction(action);
        }
      };

      // Enforce delay bounds
      const delay = Math.min(action.delayMs ?? 0, MAX_ACTION_DELAY_MS);

      if (delay > 0) {
        const timeout = setTimeout(() => {
          pendingTimeoutsRef.current.delete(timeout);
          executeAction();
        }, delay);
        pendingTimeoutsRef.current.add(timeout);
      } else {
        executeAction();
      }
    },
    [
      navigateTo,
      executeComputeAction,
      executeFileWriteAction,
      executeFileReadAction,
      executeFileDownloadAction,
    ],
  );

  // Update ref when executeClientAction changes
  useEffect(() => {
    executeClientActionRef.current = executeClientAction;
  }, [executeClientAction]);

  // Execute a sequence of actions by their IDs
  const executeActionSequence = useCallback(
    (actionIds: string[], payload: Record<string, unknown> = {}) => {
      if (!ui?.pages?.[currentPage]?.actions) return;

      const pageActions = ui.pages[currentPage].actions as Record<string, ActionDefinition>;

      for (const actionId of actionIds) {
        const action = pageActions[actionId];
        if (!action) {
          console.warn(`Action not found: ${actionId}`);
          continue;
        }

        // Handle client-side actions directly
        if (
          action.type === 'updateContext' ||
          action.type === 'navigate' ||
          action.type === 'compute' ||
          action.type === 'aiInvoke' ||
          action.type === 'fileWrite' ||
          action.type === 'fileRead' ||
          action.type === 'fileDownload'
        ) {
          executeClientAction(action, payload);
        } else {
          // For server-side actions (submit, invoke), delegate to triggerAction
          triggerAction(actionId, payload);
        }
      }
    },
    [ui, currentPage, executeClientAction, triggerAction],
  );

  // Execute onLoad actions when the current page changes
  useEffect(() => {
    if (!ui || !currentPage) return;

    const page = ui.pages?.[currentPage];
    if (!page?.onLoad) return;

    // Only run onLoad once per page per session
    if (pagesLoadedRef.current.has(currentPage)) return;
    pagesLoadedRef.current.add(currentPage);

    const onLoadIds = Array.isArray(page.onLoad) ? page.onLoad : [page.onLoad];
    const pageActions = page.actions as Record<string, ActionDefinition> | undefined;
    if (!pageActions) return;

    for (const actionId of onLoadIds) {
      const action = pageActions[actionId];
      if (action) {
        executeClientAction(action, {});
      } else {
        console.warn(`onLoad action not found: ${actionId}`);
      }
    }
  }, [currentPage, ui, executeClientAction]);

  // Context values
  const archeContextValue: ArcheContextValue = useMemo(
    () => ({
      ui,
      currentPage,
      context: bindingContext,
      requestId,
      archId: archeId,
      archeName: externalArcheName || arche?.name || '',
      isLoading,
      error,
    }),
    [
      ui,
      currentPage,
      bindingContext,
      requestId,
      archeId,
      externalArcheName,
      arche,
      isLoading,
      error,
    ],
  );

  const actionContextValue: ArcheActionValue = useMemo(
    () => ({
      triggerAction,
      executeActionSequence,
      submitForm,
      updateInput,
      isExecuting,
      lastError: actionError,
      isAIStreaming,
      aiStreamingText,
      aiThinkingText,
      stopAIStreaming,
    }),
    [
      triggerAction,
      executeActionSequence,
      submitForm,
      updateInput,
      isExecuting,
      actionError,
      isAIStreaming,
      aiStreamingText,
      aiThinkingText,
      stopAIStreaming,
    ],
  );

  const navigationContextValue: ArcheNavigationValue = useMemo(
    () => ({
      navigateTo,
      goBack,
      history: pageHistory,
      canGoBack: pageHistory.length > 0,
    }),
    [navigateTo, goBack, pageHistory],
  );

  return (
    <ArcheContext.Provider value={archeContextValue}>
      <ArcheActionContext.Provider value={actionContextValue}>
        <ArcheNavigationContext.Provider value={navigationContextValue}>
          {children}
        </ArcheNavigationContext.Provider>
      </ArcheActionContext.Provider>
    </ArcheContext.Provider>
  );
}

export default ArcheProvider;
